import { inject, Component, input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy, DestroyRef, signal, effect, output, model } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { DatePipe } from '@angular/common';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PesquisarEscolaDialogComponent, PesquisaEscolaResult } from './dialogs/pesquisar-escola/pesquisar-escola';
import { EspecialidadesRelacionadasDialogComponent, EspecialidadeRelacionadaRow } from './dialogs/especialidades-relacionadas/especialidades-relacionadas';
import { FormacaoService as FormacaoCoreService } from '@/core/api/sicom-api/registro/medico/formacao/formacao.service';
import { FormacaoRequest } from '@/core/api/sicom-api/registro/medico/formacao/formacao.model';
import { EscolaService } from '@/core/api/sicom-api/registro/medico/escola/escola.service';
import { PaisService } from '@/core/api/sicom-api/registro/medico/pais/pais.service';
import { CursosPosEntityResponse } from '@/core/api/sicom-api/registro/medico/escola/escola-cursos-pos-entity.model';
import { PosGraduacaoResponse } from '@/core/api/sicom-api/registro/medico/formacao/pos-graduacao.model';
import { MessageService } from 'primeng/api';
import { buildGraduacaoPayload, buildPosPayload, FormacaoGradFormValue, FormacaoPosFormValue } from './formacao.mapper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormacaoGraduacaoComponent } from './dialogs/graduacao/formacao-graduacao.component';
import { FormacaoPosComponent } from './dialogs/pos-graduacao/formacao-pos.component';
import { FormacaoFacade } from './formacao.facade';
import { BaseEditableForm } from '@/shared/forms/base-editable-form';
import { CardContent } from '@/shared/components/card-content/card-content';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { DateUtils } from '@/shared/utils/date-utils';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { AppMenuItem } from '@/core/permissions/permission.model';

export type FormacaoTableData = {
    displayType: string;
    displayInstitution: string;
    displayDate: string;
};

export type FormacaoListItem = {
    _type: 'GRAD' | 'POS';
    _index: number;
    table: FormacaoTableData | undefined;
} & (FormacaoRequest | PosGraduacaoResponse | undefined);

@Component({
    selector: 'app-formacao',
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, Button, Tooltip, CardContent, PesquisarEscolaDialogComponent, EspecialidadesRelacionadasDialogComponent, FormacaoGraduacaoComponent, FormacaoPosComponent, BotaoAcoes, CanDirective],
    providers: [DatePipe],
    templateUrl: './formacao.component.html',
    styleUrl: './formacao.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Aba de formação: orquestra graduação/pós, países, tipos e diplomas. */
export class FormacaoComponent extends BaseEditableForm implements OnInit, OnChanges {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    selectedDetail = input<FormacaoListItem>();
    voltar = output<void>();
    abrirDialog = output<void>();

    modoEdicao = model<boolean>(false);
    modoCreate = signal<boolean>(false);

    private snapshotFormValue: Record<string, unknown> | null = null;

    formacaoCarregando: boolean = false;
    formacaoErro?: string;
    prazoDiploma: string = '';
    prorrogarPrazoCarregando = signal(false);
    paisesOptions: Array<{ label: string; value: number }> = [];
    paisesCarregando = signal(false);

    private comprovanteGraduacaoPendente: number | null = null;

    tipoFormacaoOptions: Array<{ label: string; value: number }> = [];
    comprovanteGraduacaoOptions: Array<{ label: string; value: number }> = [];

    tiposFormacaoCarregando: boolean = false;
    tiposFormacaoErro?: string;

    diplomasCarregando = signal(false);
    diplomasErro?: string;

    private registroFormacao: FormacaoRequest | null = null;

    private registroPos: PosGraduacaoResponse | null = null;
    posCarregando: boolean = false;

    displayPesquisarEscola = false;
    pesquisarEscolaTipo: 'GRAD' | 'POS' = 'GRAD';

    displayEspecialidadesDialog = signal(false);
    itensEspecialidadesRelacionadas: EspecialidadeRelacionadaRow[] = [];

    posDenominacaoOptions: Array<{ label: string; value: number }> = [];
    posDenominacoesCarregando = false;

    protected readonly roleFormacaoCriar = PERMISSOES.medicos.formacao.criar;

    get acoesFormulario(): AppMenuItem[] {
        const editando = this.modoEdicao();
        return [
            acaoMenuItem({
                id: 'editar',
                label: 'Editar',
                icon: 'pi pi-pencil',
                cor: 'editar',
                disabled: editando,
                permission: this.roleFormacaoCriar,
                command: () => this.habilitarEdicao()
            }),
            acaoMenuItem({
                id: 'remover',
                label: 'Remover',
                icon: 'pi pi-trash',
                cor: 'remover',
                disabled: editando,
                permission: PERMISSOES.medicos.formacao.excluir,
                command: () => this.remover()
            })
        ];
    }

    /**
     * Ver acoesFormulario: Salvar e Cancelar saem do menu e viram ação em destaque.
     * A role é `criar` porque o catálogo não tem `medicos.formacao.editar` — criar e editar
     * uma formação compartilham a mesma role por desenho.
     */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleFormacaoCriar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', disabled: !this.formacaoForm.valid, permission: role, command: () => this.salvarDados() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelarEdicao() })
        ];
    }
    posDenominacoesErro?: string;
    private cursosPosLista: CursosPosEntityResponse[] = [];
    private posCoIesSelecionado: number | null = null;
    private readonly fb = inject(FormBuilder);
    readonly graduacaoForm = this.buildGraduacaoForm();
    readonly posForm = this.buildPosForm();
    readonly formacaoForm = this.fb.group({ graduacao: this.graduacaoForm, pos: this.posForm });

    constructor(
        private formacaoCoreService: FormacaoCoreService,
        private escolaService: EscolaService,
        private paisService: PaisService,
        private messageService: MessageService,
        private destroyRef: DestroyRef,
        private facade: FormacaoFacade,
        private datePipe: DatePipe
    ) {
        super();
        effect(() => {
            const editing = this.modoEdicao();
            if (editing) {
                this.enableEditing();
            } else {
                this.disableEditing();
            }
        });
    }

    ngOnInit(): void {
        if (this.selectedDetail()) {
            this.renderizarRegistroSelecionado();
        }
        this.carregarPaises();
        this.carregarTiposFormacao();
        this.configurarReacoesFormulario();
        this.carregarDadosFormacao();
        this.atualizarValidadoresPorAba();
        if (!this.modoEdicao()) this.preencherForm();
    }

    private buildGraduacaoForm() {
        return this.fb.group({
            tipoFormacao: this.fb.control<string | number | null>({ value: '', disabled: true }, Validators.required),
            gradEscolaId: this.fb.control<string | number | null>({ value: '', disabled: true }),
            gradEscolaUf: [{ value: '', disabled: true }],
            nomeEscola: [{ value: '', disabled: true }, Validators.required],
            nomeAnteriorEscola: [{ value: '', disabled: true }],
            comprovanteGraduacao: this.fb.control<string | number | null>({ value: '', disabled: true }, Validators.required),
            idTipoDiploma: this.fb.control<number | null>({ value: null, disabled: true }),
            dataCriacaoEscola: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            dataCriacaoCurso: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            dataInicioCurso: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            dataConclusaoCurso: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            dataFormatura: [{ value: '', disabled: true }, [Validators.required, this.dataMenorOuIgualHojeValidator()]],
            expedirDiploma: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            cargaHoraria: [{ value: '', disabled: true }],
            tese: [{ value: '', disabled: true }],
            numeroProcessoDiploma: [{ value: '', disabled: true }],
            livroRegistroDiploma: [{ value: '', disabled: true }],
            numeroFolhaRegistro: [{ value: '', disabled: true }],
            numeroRegistroMEC: [{ value: '', disabled: true }],
            dataRegistroMEC: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            autorizaDivulgacao: [true],
            apresentouDiploma: [true],
            paisOrigemFaculdade: this.fb.control<number | null>({ value: null, disabled: true }),
            nomeFaculdadeEstrangeira: [{ value: '', disabled: true }],
            urlDocumentosComprobatorios: [{ value: '', disabled: true }],
            idPrestadorIes: this.fb.control<number | null>({ value: null, disabled: true }),
            ufPrestadorIes: [{ value: '', disabled: true }],
            nomeCurso: [{ value: '', disabled: true }],
            idLocalDiploma: this.fb.control<number | null>({ value: null, disabled: true }),
            codigoValidacaoDiploma: [{ value: '', disabled: true }],
            urlValidacaoDiploma: [{ value: '', disabled: true }],
            nomeXmlDiploma: [{ value: '', disabled: true }],
            nomeRelatorioDiploma: [{ value: '', disabled: true }],
            nomeRepresentacaoGraficaDiploma: [{ value: '', disabled: true }]
        });
    }

    private buildPosForm() {
        return this.fb.group({
            posIesId: this.fb.control<string | number | null>({ value: '', disabled: true }),
            posIesUf: [{ value: '', disabled: true }],
            posNome: [{ value: '', disabled: true }, Validators.required],
            posDenominacaoCurso: this.fb.control<string | number | null>({ value: '', disabled: true }, Validators.required),
            posComprovanteGraduacao: this.fb.control<string | number | null>({ value: '', disabled: true }),
            posDtInicioOferta: [{ value: '', disabled: true }, [this.dataMenorOuIgualHojeValidator()]],
            posDtInicioCurso: [{ value: '', disabled: true }, [Validators.required, this.dataMenorOuIgualHojeValidator()]],
            posDtFimCurso: [{ value: '', disabled: true }, [Validators.required, this.dataMenorOuIgualHojeValidator()]],
            posDtFormatura: [{ value: '', disabled: true }, [Validators.required, this.dataMenorOuIgualHojeValidator()]],
            posCargaHoraria: [{ value: '', disabled: true }],
            posIdDocumento: [{ value: '', disabled: true }],
            posUrlDocumento: [{ value: '', disabled: true }]
        });
    }

    abrirEspecialidadesRelacionadas(): void {
        const codCurso: number | null | undefined = this.formacaoForm.get('pos.posDenominacaoCurso')?.value as number | null | undefined;
        if (codCurso == null) {
            this.itensEspecialidadesRelacionadas = [];
            this.displayEspecialidadesDialog.set(true);
            return;
        }

        this.escolaService.listarPosEspecialidadesPorCurso(Number(codCurso)).subscribe({
            next: (lista) => {
                this.itensEspecialidadesRelacionadas = (lista || []).map((e) => ({
                    especialidade: e?.dsEspecialidade || '',
                    areaAtuacao: e?.dsAreaAtuacao || ''
                }));
                this.displayEspecialidadesDialog.set(true);
            },
            error: (err) => {
                this.logError('Erro ao listar especialidades por curso de pós', err);
                this.itensEspecialidadesRelacionadas = [];
                this.displayEspecialidadesDialog.set(true);
            }
        });
    }

    onEspecialidadesDialogClosed(): void {}

    preencherForm() {
        this.atualizarValidadoresPorAba();
        this.diplomasErro = undefined;

        if (this.selectedDetail()?._type === 'POS') {
            if (this.modoEdicao()) {
                this.formacaoForm.get('pos.posComprovanteGraduacao')?.setValue(null);
            }
            this.comprovanteGraduacaoOptions = [];
            this.carregarComprovantesPos();
            if (!this.modoEdicao()) {
                if (this.registroPos) {
                    this.carregarDadosPosGraduacao(true);
                } else {
                    this.renderizarRegistroAtual();
                }
            }
        } else {
            this.comprovanteGraduacaoOptions = [];
            const idTipoFormacao = this.formacaoForm.get('graduacao.tipoFormacao')?.value as number | string | null | undefined;
            if (this.modoEdicao()) {
                this.formacaoForm.get('graduacao.comprovanteGraduacao')?.setValue(null);
            }
            if (idTipoFormacao != null && idTipoFormacao !== '') {
                this.carregarDiplomasPorTipoFormacao(Number(idTipoFormacao));
            }
            if (!this.modoEdicao()) {
                this.renderizarRegistroAtual();
            }
        }
    }

    remover(): void {
        const pessoaAtual = this.pessoa();
        const registro = this.selectedDetail();
        if (!pessoaAtual?.id || !pessoaAtual?.uf || !registro) {
            this.messageService.add({ severity: 'warn', summary: 'Remover', detail: 'Selecione um registro para remover.' });
            return;
        }

        if (registro._type === 'GRAD') {
            const tipoFormacao = (registro as FormacaoRequest).tipoFormaIdTipoFormacao;
            if (!tipoFormacao) return;
            this.formacaoCoreService.removerFormacao({ id: pessoaAtual.id, uf: pessoaAtual.uf, tipoFormacao }).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Removido', detail: 'Graduação removida com sucesso.' });
                    this.voltar.emit();
                }
            });
            return;
        }

        const coCursoEspecializacao = (registro as PosGraduacaoResponse).coCursoEspecializacao;
        if (!coCursoEspecializacao) return;
        this.formacaoCoreService.removerPosGraduacao({ id: pessoaAtual.id, uf: pessoaAtual.uf, coCursoEspecializacao }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Removido', detail: 'Pós-Graduação removida com sucesso.' });
                this.voltar.emit();
            }
        });
    }
    private atualizarValidadoresPorAba(): void {
        const gradGroup = this.formacaoForm.get('graduacao');
        const posGroup = this.formacaoForm.get('pos');
        if (this.selectedDetail()?._type === 'GRAD') {
            gradGroup?.enable({ emitEvent: false });
            posGroup?.disable({ emitEvent: false });
        } else {
            posGroup?.enable({ emitEvent: false });
            gradGroup?.disable({ emitEvent: false });
        }
        this.formacaoForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    }

    protected isSalvarDisabled(): boolean {
        if (!this.modoEdicao()) return true;
        return this.formacaoForm.invalid;
    }

    habilitarEdicao(): void {
        this.snapshotFormValue = this.formacaoForm.getRawValue();
        // this.snapshotAbaSelecionada = this.abaSelecionada;
        this.modoEdicao.set(true);
        this.enableEditing();
    }

    novo(): void {
        this.modoCreate.set(true);
        this.habilitarEdicao();
        this.comprovanteGraduacaoPendente = null;
        this.comprovanteGraduacaoOptions = [];
        this.posDenominacaoOptions = [];
        this.cursosPosLista = [];
        this.posCoIesSelecionado = null;
        this.diplomasErro = undefined;
        this.posDenominacoesErro = undefined;

        this.formacaoForm.patchValue(
            {
                graduacao: {
                    tipoFormacao: '',
                    comprovanteGraduacao: null,
                    idTipoDiploma: null,
                    gradEscolaId: null,
                    gradEscolaUf: null,
                    nomeEscola: '',
                    nomeAnteriorEscola: '',
                    dataCriacaoEscola: '',
                    dataCriacaoCurso: '',
                    dataInicioCurso: '',
                    dataConclusaoCurso: '',
                    dataFormatura: '',
                    expedirDiploma: '',
                    cargaHoraria: '',
                    tese: '',
                    numeroProcessoDiploma: '',
                    livroRegistroDiploma: '',
                    numeroFolhaRegistro: '',
                    numeroRegistroMEC: '',
                    dataRegistroMEC: '',
                    autorizaDivulgacao: true,
                    apresentouDiploma: true,
                    paisOrigemFaculdade: null,
                    nomeFaculdadeEstrangeira: '',
                    urlDocumentosComprobatorios: '',
                    idPrestadorIes: null,
                    ufPrestadorIes: '',
                    nomeCurso: '',
                    idLocalDiploma: null,
                    codigoValidacaoDiploma: '',
                    urlValidacaoDiploma: '',
                    nomeXmlDiploma: '',
                    nomeRelatorioDiploma: '',
                    nomeRepresentacaoGraficaDiploma: ''
                },
                pos: {
                    posIesId: null,
                    posIesUf: null,
                    posNome: '',
                    posDenominacaoCurso: null,
                    posComprovanteGraduacao: null,
                    posDtInicioOferta: '',
                    posDtInicioCurso: '',
                    posDtFimCurso: '',
                    posDtFormatura: '',
                    posCargaHoraria: '',
                    posIdDocumento: '',
                    posUrlDocumento: ''
                }
            },
            { emitEvent: false }
        );

        if (this.selectedDetail()?._type === 'POS') {
            this.carregarComprovantesPos();
        }
    }

    salvarDados(): void {
        if (this.selectedDetail()?._type === 'GRAD') {
            this.salvarGraduacao();
        } else {
            this.salvarPos();
        }
    }

    prorrogarPrazoDiploma(): void {
        if (this.prorrogarPrazoCarregando()) return;

        const pessoaAtual = this.pessoa();
        if (!pessoaAtual?.id || !pessoaAtual?.uf) {
            this.logger.error('Pessoa não informada para prorrogar prazo do diploma.');
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Pessoa não informada.' });
            return;
        }

        this.prorrogarPrazoCarregando.set(true);
        this.formacaoCoreService.prorrogarPrazoDiploma({ id: pessoaAtual.id, uf: pessoaAtual.uf }).subscribe({
            next: (resp) => {
                const novaData = resp?.dtPrazoDiploma ? this.formatarData(resp.dtPrazoDiploma) : '';
                if (novaData) {
                    this.prazoDiploma = novaData;
                } else {
                    this.carregarDadosFormacao();
                }

                const aviso = resp?.prazoMaximoAtingido === true || (!!resp?.mensagem && !resp?.dtPrazoDiploma);
                const detalhe = resp?.mensagem || (aviso ? 'Prazo máximo atingido para prorrogação do diploma.' : 'Prazo do diploma prorrogado com sucesso.');
                this.messageService.add({
                    severity: aviso ? 'warn' : 'success',
                    summary: aviso ? 'Atenção' : 'Operação realizada',
                    detail: detalhe,
                    life: 3000
                });
                this.prorrogarPrazoCarregando.set(false);
            },
            error: (err) => {
                // Sem toast local: o interceptor global já exibiu a mensagem do backend. Aqui fica só o
                // que precisa ser desfeito no estado da tela.
                this.logger.error('Erro ao prorrogar prazo do diploma:', err);
                this.prorrogarPrazoCarregando.set(false);
            }
        });
    }

    private salvarGraduacao(): void {
        const gradGroup = this.formacaoForm.get('graduacao');
        gradGroup?.markAllAsTouched();
        gradGroup?.updateValueAndValidity({ emitEvent: false });
        if (gradGroup?.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Formulário Inválido', detail: 'Revise os campos obrigatórios e as datas (devem ser menores ou iguais a hoje).' });
            return;
        }

        const pessoaAtual = this.pessoa();
        if (!pessoaAtual?.id || !pessoaAtual?.uf) {
            this.logger.error('Pessoa não informada para salvar formation.');
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Pessoa não informada.' });
            return;
        }

        const v = (this.formacaoForm.get('graduacao')?.value || {}) as FormacaoGradFormValue;
        const payload = buildGraduacaoPayload(pessoaAtual, v);

        this.formacaoCoreService.salvarFormacao(payload).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Operação realizada', detail: 'Formação de Graduação salva com sucesso.', life: 3000 });
                this.snapshotFormValue = null;
                if (this.modoCreate()) {
                    // Novo registro concluído: volta para a lista/card de formação.
                    this.modoCreate.set(false);
                    this.modoEdicao.set(false);
                    this.voltar.emit();
                    return;
                }
                this.carregarDadosFormacao();
                this.disableEditing();
                this.modoEdicao.set(false);
            },
            error: (err) => {
                // Sem toast local; o modo de edição permanece para o usuário corrigir.
                this.logger.error('Erro ao salvar formação de graduação:', err);
            }
        });
    }

    private salvarPos(): void {
        const posGroup = this.formacaoForm.get('pos');
        posGroup?.markAllAsTouched();
        posGroup?.updateValueAndValidity({ emitEvent: false });
        if (posGroup?.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Formulário Inválido', detail: 'Revise os campos obrigatórios e as datas (devem ser menores ou iguais a hoje).' });
            return;
        }

        const pessoaAtual = this.pessoa();
        if (!pessoaAtual?.id || !pessoaAtual?.uf) {
            this.logger.error('Pessoa não informada para salvar pós-graduação.');
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Pessoa não informada.' });
            return;
        }

        const v = (this.formacaoForm.get('pos')?.value || {}) as FormacaoPosFormValue;

        const cursoLabelFromOptions = this.posDenominacaoOptions.find((o) => o.value === (v as { posDenominacaoCurso?: number }).posDenominacaoCurso)?.label;
        const cursoLabelFromLista = this.cursosPosLista.find((c) => c.coCursoEspecializacao === (v as { posDenominacaoCurso?: number }).posDenominacaoCurso)?.nmCursoEspecializacao as string | undefined;
        const nmCursoEspecializacao = cursoLabelFromOptions || cursoLabelFromLista || undefined;

        const payload = buildPosPayload(pessoaAtual, v, nmCursoEspecializacao);

        this.formacaoCoreService.salvarPosGraduacao(payload).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Operação realizada', detail: 'Pós-Graduação salva com sucesso.', life: 3000 });
                this.snapshotFormValue = null;
                if (this.modoCreate()) {
                    // Novo registro concluído: volta para a lista/card de formação.
                    this.modoCreate.set(false);
                    this.modoEdicao.set(false);
                    this.voltar.emit();
                    return;
                }
                this.carregarDadosPosGraduacao(true);
                this.disableEditing();
                this.modoEdicao.set(false);
            },
            error: (err) => {
                // Sem toast local; o modo de edição permanece para o usuário corrigir.
                this.logger.error('Erro ao salvar pós-graduação:', err);
            }
        });
    }

    cancelarEdicao(): void {
        const emModoCriacao = this.modoCreate();
        this.modoCreate.set(false);
        if (emModoCriacao) {
            this.modoEdicao.set(false);
            this.snapshotFormValue = null;
            this.cancelEditing();
            this.voltar.emit();
            return;
        }

        if (this.snapshotFormValue) {
            const snap = this.snapshotFormValue;
            // const abaSnap = this.snapshotAbaSelecionada;
            // this.abaSelecionada = abaSnap;
            this.formacaoForm.patchValue(snap, { emitEvent: false });

            const tipo = (snap as { graduacao?: { tipoFormacao?: unknown } } | null)?.graduacao?.tipoFormacao;
            if (this.selectedDetail()?._type === 'GRAD') {
                if (tipo != null && tipo !== '') {
                    this.carregarDiplomasPorTipoFormacao(Number(tipo));
                }
            } else {
                this.carregarComprovantesPos();
            }
        } else {
            this.renderizarRegistroAtual();
        }
        // Mantém o formulário sempre habilitado conforme requisitos desta tela
        this.modoEdicao.set(false);
        this.snapshotFormValue = null;
        this.cancelEditing();
    }

    abrirPesquisarEscola(tipo: 'GRAD' | 'POS' = 'GRAD'): void {
        this.pesquisarEscolaTipo = tipo;
        this.displayPesquisarEscola = true;
    }

    onPesquisarEscolaCanceled(): void {
        this.displayPesquisarEscola = false;
    }

    onPesquisarEscolaSaved(result: PesquisaEscolaResult): void {
        if (this.pesquisarEscolaTipo === 'POS') {
            this.formacaoForm.patchValue({
                pos: {
                    posNome: result?.nome ?? '',
                    posIesId: result?.coIes ?? result?.id ?? null,
                    posIesUf: result?.uf ?? null
                }
            });

            this.formacaoForm.get('pos.posDenominacaoCurso')?.setValue(null);
            this.posDenominacaoOptions = [];
            this.cursosPosLista = [];
            this.posDenominacoesErro = undefined;
            this.posCoIesSelecionado = result?.coIes ?? result?.id ?? null;

            if (this.posCoIesSelecionado != null) {
                this.carregarDenominacoesPosPorIes(this.posCoIesSelecionado);
            }
        } else {
            this.formacaoForm.patchValue({
                graduacao: {
                    gradEscolaId: result?.id ?? null,
                    gradEscolaUf: result?.uf ?? null,
                    nomeEscola: result?.nome ?? '',
                    nomeAnteriorEscola: result?.nomeAnterior ?? ''
                }
            });
        }
        this.displayPesquisarEscola = false;
    }

    onOpenDenominacaoSelect(): void {
        if (this.posDenominacoesCarregando) return;
        if (this.posDenominacaoOptions && this.posDenominacaoOptions.length > 0) return;
        if (this.posCoIesSelecionado == null) return;
        this.carregarDenominacoesPosPorIes(this.posCoIesSelecionado);
    }

    private carregarDenominacoesPosPorIes(coIes: number): void {
        this.posDenominacoesCarregando = true;
        this.facade.cursosPosPorIes$(coIes).subscribe({
            next: ({ lista, options }) => {
                this.cursosPosLista = lista || [];
                this.posDenominacaoOptions = options || [];
                this.posDenominacoesCarregando = false;
            },
            error: (err) => {
                this.logError('Erro ao carregar denominações de cursos de pós por IES', err);
                this.posDenominacoesErro = 'Não foi possível carregar as denominações do curso.';
                this.posDenominacoesCarregando = false;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p?.id && p?.uf) {
                this.carregarDadosFormacao();
                if (this.selectedDetail()?._type === 'POS') {
                    this.carregarDadosPosGraduacao(true);
                }
            }
        }
        if (changes['selectedDetail'] && this.selectedDetail() && this.formacaoForm) {
            if (this.selectedDetail()?._index !== -1) {
                this.entrarModoVisualizacao();
            }
            this.renderizarRegistroSelecionado();
        }
    }

    private renderizarRegistroSelecionado() {
        const detail = this.selectedDetail();
        if (!detail) return;

        if (detail._index === -1) {
            this.atualizarValidadoresPorAba();
            this.novo();
            return;
        }

        this.entrarModoVisualizacao();
        this.renderizarRegistroAtual();
    }

    private entrarModoVisualizacao(): void {
        this.modoCreate.set(false);
        this.modoEdicao.set(false);
        this.snapshotFormValue = null;
        this.cancelEditing();
    }

    private carregarDadosFormacao(): void {
        const pessoaAtual = this.pessoa();
        if (!pessoaAtual?.id || !pessoaAtual?.uf) return;

        this.formacaoCarregando = true;
        this.formacaoErro = undefined;
        const registro = this.selectedDetail();
        if (registro?._index === -1) {
            this.registroFormacao = null;
            this.prazoDiploma = '';
            this.formacaoCarregando = false;
            return;
        }
        this.registroFormacao = registro as FormacaoRequest;
        if (registro) {
            this.preencherFormularioGraduacao(registro as FormacaoRequest);
        } else {
            this.prazoDiploma = '';
        }
        this.formacaoCarregando = false;
    }

    private carregarDadosPosGraduacao(renderPrimeiro: boolean = false): void {
        const pessoaAtual = this.pessoa();
        if (!pessoaAtual?.id || !pessoaAtual?.uf) return;

        this.posCarregando = true;
        if (this.selectedDetail()?._index === -1) {
            this.registroPos = null;
            this.posCarregando = false;
            return;
        }

        this.registroPos = this.selectedDetail() as PosGraduacaoResponse;
        this.renderizarRegistroAtual();
    }

    private renderizarRegistroAtual(): void {
        if (this.selectedDetail()?._type === 'POS') {
            const registroPos = this.registroPos;
            if (registroPos) this.preencherFormularioPos(registroPos);
            return;
        }
        const registro = this.registroFormacao;
        if (registro) {
            this.preencherFormularioGraduacao(registro);
        } else {
            this.prazoDiploma = '';
        }
    }

    private preencherFormularioGraduacao(f: FormacaoRequest): void {
        if (!this.formacaoForm) return;

        // Campos de diploma/escola que o backend agrega ao FormacaoRequest mas não constam no tipo base.
        const fRegistro = f as FormacaoRequest & {
            escolaIdEscola?: number;
            escolaUfSgUf?: string;
            idTipoDiploma?: number;
            idLocal?: number;
            coValidacaoDiploma?: string;
            txUrlValidacao?: string;
            nmXml?: string;
            nmRelConfirmacao?: string;
            nmRepGrafica?: string;
        };

        this.prazoDiploma = this.formatarData(f.dtPrazoDiploma);
        this.comprovanteGraduacaoPendente = f.idTipoFormacaoDiploma != null ? Number(f.idTipoFormacaoDiploma) : null;

        this.formacaoForm.patchValue(
            {
                graduacao: {
                    gradEscolaId: fRegistro?.escolaIdEscola ?? '',
                    gradEscolaUf: fRegistro?.escolaUfSgUf ?? '',
                    nomeEscola: f.nmEscola ?? '',
                    nomeAnteriorEscola: f.nmAnterior ?? '',
                    idTipoDiploma: fRegistro?.idTipoDiploma ?? null,
                    dataCriacaoEscola: this.formatarData(f.dtCriacao),
                    dataCriacaoCurso: this.formatarData(f.dtCriacaoCurso),
                    dataInicioCurso: this.formatarData(f.dtInicioCurso),
                    dataConclusaoCurso: this.formatarData(f.dtConclusaoCurso),
                    dataFormatura: this.formatarData(f.dtFormatura),
                    expedirDiploma: this.formatarData(f.dtExpedicaoDiploma),
                    cargaHoraria: f.txCargaHoraria ?? '',
                    tese: f.txTese ?? '',
                    numeroProcessoDiploma: f.nuProcessoDiploma ?? '',
                    livroRegistroDiploma: f.nuLivroDiploma ?? '',
                    numeroFolhaRegistro: f.nuFolhaDiploma ?? '',
                    numeroRegistroMEC: f.nuRegistroMec ?? '',
                    dataRegistroMEC: this.formatarData(f.dtRegistroMec),
                    autorizaDivulgacao: f.inDivulga === 'S',
                    apresentouDiploma: f.inApresentouDiploma !== 'N',
                    paisOrigemFaculdade: f.idPais != null ? Number(f.idPais) : null,
                    nomeFaculdadeEstrangeira: f.nmFaculdadeEstrangeira ?? '',
                    urlDocumentosComprobatorios: f.imDocumento ?? '',
                    idPrestadorIes: f.idPrestadorIes ?? null,
                    ufPrestadorIes: f.sgUfPrestadorIes ?? '',
                    nomeCurso: f.nmCurso ?? '',
                    idLocalDiploma: fRegistro?.idLocal ?? null,
                    codigoValidacaoDiploma: fRegistro?.coValidacaoDiploma ?? '',
                    urlValidacaoDiploma: fRegistro?.txUrlValidacao ?? '',
                    nomeXmlDiploma: fRegistro?.nmXml ?? '',
                    nomeRelatorioDiploma: fRegistro?.nmRelConfirmacao ?? '',
                    nomeRepresentacaoGraficaDiploma: fRegistro?.nmRepGrafica ?? ''
                }
            },
            { emitEvent: false }
        );

        if (f.tipoFormaIdTipoFormacao != null) {
            this.formacaoForm.get('graduacao.tipoFormacao')?.setValue(Number(f.tipoFormaIdTipoFormacao));
        }
    }

    private preencherFormularioPos(r: PosGraduacaoResponse): void {
        if (!this.formacaoForm) return;

        this.carregarComprovantesPos();

        if ((!this.posDenominacaoOptions || this.posDenominacaoOptions.length === 0) && r.coCursoEspecializacao != null) {
            const label = r.nmCursoEspecializacao ? String(r.nmCursoEspecializacao) : String(r.coCursoEspecializacao);
            this.posDenominacaoOptions = [
                {
                    label,
                    value: r.coCursoEspecializacao
                }
            ];
        }

        this.formacaoForm.patchValue(
            {
                pos: {
                    posNome: r.nmEscola ?? '',
                    posDenominacaoCurso: r.coCursoEspecializacao ?? null,
                    posComprovanteGraduacao: r.idTipoFormacaoDiploma ?? null,
                    posIesId: r.coIes ?? null,
                    posDtInicioOferta: this.formatarData(r.dtInicioOferta),
                    posDtInicioCurso: this.formatarData(r.dtInicio),
                    posDtFimCurso: this.formatarData(r.dtFim),
                    posDtFormatura: this.formatarData(r.dtFormatura),
                    posCargaHoraria: r.qtCargaHoraria != null ? String(r.qtCargaHoraria) : '',
                    posIdDocumento: r.idDocumento != null ? String(r.idDocumento) : '',
                    posUrlDocumento: r.txUrlDocumento ?? ''
                }
            },
            { emitEvent: false }
        );

        this.posCoIesSelecionado = r.coIes ?? null;
        this.posDenominacoesErro = undefined;
    }

    private formatarData(iso?: string): string {
        if (iso) {
            const date = this.datePipe.transform(iso, 'dd/MM/yyyy');
            return date ?? '';
        }
        return '';
    }

    private configurarReacoesFormulario(): void {
        const ctrl = this.formacaoForm.get('graduacao.tipoFormacao');
        if (!ctrl) return;

        ctrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((idTipoFormacao: number | string | null | undefined) => {
            if (this.selectedDetail()?._type === 'POS') {
                return;
            }
            this.formacaoForm.get('graduacao.comprovanteGraduacao')?.setValue(null);
            this.comprovanteGraduacaoOptions = [];
            this.diplomasErro = undefined;

            if (idTipoFormacao === null || idTipoFormacao === undefined || idTipoFormacao === '') {
                this.diplomasCarregando.set(false);
                return;
            }

            this.carregarDiplomasPorTipoFormacao(Number(idTipoFormacao));
        });

        this.formacaoForm
            .get('graduacao.comprovanteGraduacao')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((idTipoFormacaoDiploma: number | string | null | undefined) => {
                const id = idTipoFormacaoDiploma == null || idTipoFormacaoDiploma === '' ? null : Number(idTipoFormacaoDiploma);
                const opcao = id == null ? undefined : (this.comprovanteGraduacaoOptions as Array<{ value: number; idTipoDiploma?: number }>).find((item) => Number(item.value) === id);
                this.formacaoForm.get('graduacao.idTipoDiploma')?.setValue(opcao?.idTipoDiploma ?? null, { emitEvent: false });
                this.formacaoForm.get('graduacao.apresentouDiploma')?.setValue(id !== 3, { emitEvent: false });
            });
    }

    private carregarDiplomasPorTipoFormacao(idTipoFormacao: number): void {
        const tipoSolicitado = Number(idTipoFormacao);
        this.diplomasCarregando.set(true);
        this.facade.diplomasPorTipoOptions$(tipoSolicitado).subscribe({
            next: (lista) => {
                const tipoAtual = Number(this.formacaoForm.get('graduacao.tipoFormacao')?.value);
                if (tipoAtual !== tipoSolicitado) {
                    return;
                }

                this.comprovanteGraduacaoOptions = lista || [];
                if (this.comprovanteGraduacaoPendente != null) {
                    const comprovantePendente = Number(this.comprovanteGraduacaoPendente);
                    const existe = this.comprovanteGraduacaoOptions.some((opt) => Number(opt.value) === comprovantePendente);
                    if (existe) {
                        this.formacaoForm.get('graduacao.comprovanteGraduacao')?.setValue(comprovantePendente, { emitEvent: false });
                        const opcao = (this.comprovanteGraduacaoOptions as Array<{ value: number; idTipoDiploma?: number }>).find((item) => Number(item.value) === comprovantePendente);
                        this.formacaoForm.get('graduacao.idTipoDiploma')?.setValue(opcao?.idTipoDiploma ?? null, { emitEvent: false });
                        this.formacaoForm.get('graduacao.apresentouDiploma')?.setValue(comprovantePendente !== 3, { emitEvent: false });
                        this.comprovanteGraduacaoPendente = null;
                    }
                }
                const comprovanteAtual = this.formacaoForm.get('graduacao.comprovanteGraduacao')?.value;
                if ((comprovanteAtual == null || comprovanteAtual === '') && tipoSolicitado === 7 && this.comprovanteGraduacaoOptions.length === 1) {
                    this.formacaoForm.get('graduacao.comprovanteGraduacao')?.setValue(this.comprovanteGraduacaoOptions[0].value, { emitEvent: false });
                    this.formacaoForm.get('graduacao.idTipoDiploma')?.setValue((this.comprovanteGraduacaoOptions[0] as { idTipoDiploma?: number }).idTipoDiploma ?? null, { emitEvent: false });
                    this.formacaoForm.get('graduacao.apresentouDiploma')?.setValue(Number(this.comprovanteGraduacaoOptions[0].value) !== 3, { emitEvent: false });
                    this.comprovanteGraduacaoPendente = null;
                }
                this.diplomasCarregando.set(false);
            },
            error: (err) => {
                this.logError('Erro ao carregar tipos de diploma para tipo de formação', err);
                this.diplomasErro = 'Não foi possível carregar os comprovantes de graduação.';
                this.diplomasCarregando.set(false);
            }
        });
    }

    private carregarComprovantesPos(): void {
        this.diplomasCarregando.set(true);
        this.facade.comprovantesPosOptions$().subscribe({
            next: (lista) => {
                this.comprovanteGraduacaoOptions = lista || [];
                this.diplomasCarregando.set(false);
            },
            error: (err) => {
                this.logError('Erro ao carregar comprovantes de pós-graduação', err);
                this.diplomasErro = 'Não foi possível carregar os comprovantes de pós-graduação.';
                this.diplomasCarregando.set(false);
            }
        });
    }

    private carregarTiposFormacao(): void {
        this.tiposFormacaoCarregando = true;
        this.tiposFormacaoErro = undefined;
        this.facade.tiposFormacaoOptions$().subscribe({
            next: (lista) => {
                this.tipoFormacaoOptions = lista || [];
                // As opções do Tipo de Formação chegam de forma assíncrona, normalmente DEPOIS de o
                // valor do registro já ter sido aplicado ao form. Reaplicamos o valor aqui para o
                // <p-select> exibir o rótulo correto (ver reaplicarTipoFormacaoSelecionado).
                this.reaplicarTipoFormacaoSelecionado();
                this.tiposFormacaoCarregando = false;
            },
            error: (err) => {
                this.logError('Erro ao carregar tipos de formação', err);
                this.tiposFormacaoErro = 'Não foi possível carregar os tipos de formação.';
                this.tiposFormacaoCarregando = false;
            }
        });
    }

    /**
     * Reaplica o Tipo de Formação do registro após as opções do dropdown terem sido carregadas.
     *
     * O rótulo do `<p-select>` (PrimeNG 21) é um `computed` que reavalia apenas quando `modelValue`
     * muda — ele lê `options` como propriedade comum (não-reativa). Quando o valor é definido antes
     * de as opções chegarem, o rótulo fica em branco ("Selecione o tipo de formação") e não se
     * atualiza sozinho quando as opções carregam. Alternar `null -> valor` força o `modelValue` a
     * mudar e o rótulo a ser recalculado, agora com as opções presentes.
     */
    private reaplicarTipoFormacaoSelecionado(): void {
        if (this.modoCreate()) return;
        const detail = this.selectedDetail();
        if (detail?._type !== 'GRAD') return;
        const tipoSelecionado = (detail as FormacaoRequest).tipoFormaIdTipoFormacao;
        if (tipoSelecionado == null) return;
        const ctrl = this.formacaoForm.get('graduacao.tipoFormacao');
        if (!ctrl) return;
        ctrl.setValue(null, { emitEvent: false });
        ctrl.setValue(Number(tipoSelecionado), { emitEvent: false });
    }

    private carregarPaises(): void {
        this.paisesCarregando.set(true);
        this.paisService
            .listarPaises()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) => {
                    this.paisesOptions = (lista || []).map((p) => ({ label: p.nmPais, value: Number(p.id) })).sort((a, b) => a.label.localeCompare(b.label));
                    this.paisesCarregando.set(false);
                },
                error: (err) => {
                    this.logError('Erro ao carregar países', err);
                    this.paisesOptions = [];
                    this.paisesCarregando.set(false);
                }
            });
    }

    private logError(msg: string, err: unknown): void {
        this.logger.error(msg, err);
    }

    private dataMenorOuIgualHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? { dataMaiorQueHojeInvalida: true } : null;
        };
    }
}
